/**
 * FAQ/Help Route
 * Comprehensive FAQ and help page
 */

import { createFileRoute } from '@tanstack/react-router';
import { SEOHead } from '@/components/seo/SEOHead';
import { StructuredData, generateFAQSchema, generateBreadcrumbSchema } from '@/components/seo/StructuredData';
import { useState } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';

function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqItems = [
    {
      question: "Is Smart Invoice Generator really free to use?",
      answer: "Yes! Smart Invoice Generator is completely free with no hidden fees, no premium tiers, and no credit card required. You can generate unlimited invoices at zero cost. We're supported by voluntary donations from users who find value in our service."
    },
    {
      question: "Do I need to create an account to generate invoices?",
      answer: "No account required! Unlike many other invoice generators, we don't force you to sign up, verify your email, or remember yet another password. Simply visit our website, fill in your invoice details, and download your professional invoice instantly."
    },
    {
      question: "What file formats can I export my invoices in?",
      answer: "You can export your invoices in both PDF and PNG formats. PDF is perfect for email attachments and professional printing, while PNG is ideal for sharing via messaging apps, social media, or embedding in documents. Both formats maintain high quality and professional appearance."
    },
    {
      question: "Can I add my company logo and branding to invoices?",
      answer: "Absolutely! You can upload your company logo, customize colors, choose from multiple professional templates, and add your branding elements. This ensures your invoices match your brand identity and look professional when sent to clients."
    },
    {
      question: "How many currencies are supported?",
      answer: "We support over 150 world currencies including USD, EUR, GBP, JPY, INR, AUD, CAD, CHF, CNY, and many more. The currency selector automatically formats amounts with the correct symbols and decimal places for your chosen currency."
    },
    {
      question: "Is my invoice data stored on your servers?",
      answer: "No, your privacy is our priority. All invoice generation happens locally in your browser using JavaScript. We don't store, transmit, or have access to your invoice content on our servers. Your sensitive business information remains on your device."
    },
    {
      question: "Can I save and manage multiple invoices?",
      answer: "Yes! We provide an invoice history feature that saves your recent invoices locally in your browser. You can access, view, and re-download previously created invoices. Your history is stored locally and can be cleared at any time."
    },
    {
      question: "How does the AI-powered invoice generation work?",
      answer: "Our AI intelligently parses the information you enter, automatically calculates line items, applies discounts and taxes, formats dates and numbers correctly, and ensures professional layout. The AI learns from common invoice patterns to create polished, business-ready documents."
    },
    {
      question: "Can I use Smart Invoice Generator for my business legally?",
      answer: "Yes, Smart Invoice Generator creates valid, professional invoices suitable for business use. However, invoice requirements vary by country and industry. Always ensure your invoices comply with local tax laws and include required elements like tax ID numbers, business registration details, and proper legal wording."
    },
    {
      question: "Does Smart Invoice Generator work on mobile devices?",
      answer: "Yes! Our service is fully responsive and optimized for mobile phones, tablets, and desktop computers. You can create professional invoices from anywhere using any device. The interface adapts perfectly to small screens while maintaining all functionality."
    },
    {
      question: "Can I edit an invoice after downloading it?",
      answer: "Once you download your invoice as a PDF or PNG, it becomes a static file that can't be edited within our tool. However, you can easily revisit your invoice history, make changes to the original, and generate a new version. We recommend keeping important invoice data accessible until payment is complete."
    },
    {
      question: "What payment terms can I include in my invoices?",
      answer: "You can customize payment terms to suit your business needs. Common options include 'Net 30' (payment due within 30 days), 'Due on Receipt' (payment due immediately), 'Net 15/60/90', or custom terms. You can also include late payment fees, early payment discounts, and specific payment instructions."
    },
    {
      question: "How do I add tax calculations to my invoices?",
      answer: "Our invoice generator makes tax calculations easy. Simply enter your tax rate as a percentage, and the system automatically calculates tax amounts for each line item and the total invoice. You can apply different tax rates, add multiple tax types (VAT, GST, sales tax), and show tax amounts clearly on the invoice."
    },
    {
      question: "Can I include discounts on my invoices?",
      answer: "Yes, you can add percentage-based or fixed-amount discounts to your invoices. The system automatically calculates discount amounts and adjusts the subtotal accordingly. You can offer early payment discounts, bulk order discounts, or promotional discounts as needed."
    },
    {
      question: "Is there a limit to how many invoices I can create?",
      answer: "No, there's no limit! Create as many invoices as you need, as often as you need. We don't impose quotas, rate limits for legitimate use, or artificial restrictions. Our service is designed to handle the needs of freelancers and small businesses of all sizes."
    },
    {
      question: "What should I do if I find a bug or technical issue?",
      answer: "We apologize for any inconvenience. Please report bugs through our contact form or directly on our GitHub repository. Include details about your browser, device, and steps to reproduce the issue. We actively maintain and improve our service based on user feedback."
    },
    {
      question: "Can I request new features or template designs?",
      answer: "Absolutely! We welcome feature requests and suggestions. Please share your ideas through our contact form or GitHub issues. We prioritize features based on user demand and feasibility. Many of our current features were suggested by users like you."
    },
    {
      question: "How do I support the development of Smart Invoice Generator?",
      answer: "We're thrilled you'd like to support us! You can make a voluntary donation through our Ko-fi page. Your support helps cover server costs, development time, and enables us to add new features. Donations are completely optional—our service remains free for everyone regardless of donation status."
    },
    {
      question: "Is Smart Invoice Generator suitable for all types of businesses?",
      answer: "Smart Invoice Generator works well for freelancers, consultants, contractors, service providers, small retailers, creative professionals, and many other business types. However, if you have complex invoicing needs like recurring billing, multi-currency projects, or integration with accounting software, you may need specialized accounting software."
    },
    {
      question: "Can I use Smart Invoice Generator offline?",
      answer: "Once our website loads in your browser, you can continue using the invoice generator without an internet connection. However, you'll need to be online to initially load the page and to access certain features like currency updates or new template designs."
    },
    {
      question: "How long does it take to generate an invoice?",
      answer: "Most users create their first invoice in under 2 minutes. Once you're familiar with the process, you can generate professional invoices in as little as 30-60 seconds. Our streamlined process and AI-powered features eliminate the complexity found in traditional invoicing software."
    },
    {
      question: "What information do I need to create an invoice?",
      answer: "To create a professional invoice, you'll need: your business name and contact information, your client's name and contact details, a description of products/services provided, quantities and unit prices, the invoice date and due date, and your preferred payment method. Having a logo handy is optional but recommended."
    },
    {
      question: "Can I customize the invoice numbering system?",
      answer: "Yes, you have full control over invoice numbering. You can use sequential numbers, custom prefixes, date-based numbering (e.g., INV-2024-001), or any system that works for your business. This helps maintain professional record-keeping and makes tracking easier."
    },
    {
      question: "How secure is my financial information?",
      answer: "Your security is paramount. Since all invoice generation happens locally in your browser, your financial information never leaves your device. We use HTTPS encryption for all data transmission, don't store sensitive data on our servers, and follow industry best practices for web security. We never see or access your invoice content."
    }
  ];

  // Schema data
  const faqSchemaData = generateFAQSchema(faqItems);
  const breadcrumbData = generateBreadcrumbSchema([
    { name: "Home", url: "https://invoicegenerated.com/" },
    { name: "FAQ & Help", url: "https://invoicegenerated.com/faq" }
  ]);

  return (
    <>
      <SEOHead
        title="FAQ & Help - Smart Invoice Generator | Common Questions Answered"
        description="Find answers to frequently asked questions about Smart Invoice Generator. Learn how to create invoices, export files, customize templates, and more. Get help with common issues."
        keywords="invoice generator FAQ, help with invoicing, how to create invoice, invoice generator guide, common invoice questions, invoice maker support"
        canonical="https://invoicegenerated.com/faq"
      />
      <StructuredData data={faqSchemaData} />
      <StructuredData data={breadcrumbData} />

      <PageLayout>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-12 max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 sm:p-12">
            {/* Page Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-10">
              Find answers to common questions about Smart Invoice Generator. Can't find what you're looking for? <a href="/contact" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium">Contact us</a> for help.
            </p>

            {/* Quick Links */}
            <section className="mb-10 bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Quick Links
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <a href="#getting-started" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium">
                  → Getting Started
                </a>
                <a href="#features" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium">
                  → Features & Capabilities
                </a>
                <a href="#privacy-security" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium">
                  → Privacy & Security
                </a>
                <a href="#technical-support" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium">
                  → Technical Support
                </a>
              </div>
            </section>

            {/* Getting Started */}
            <section id="getting-started" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                Getting Started
              </h2>
              <div className="space-y-4">
                {faqItems.slice(0, 3).map((item, index) => (
                  <FAQItem
                    key={index}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openIndex === index}
                    onToggle={() => toggleFAQ(index)}
                  />
                ))}
              </div>
            </section>

            {/* Features & Capabilities */}
            <section id="features" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                Features & Capabilities
              </h2>
              <div className="space-y-4">
                {faqItems.slice(3, 12).map((item, index) => (
                  <FAQItem
                    key={index + 3}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openIndex === index + 3}
                    onToggle={() => toggleFAQ(index + 3)}
                  />
                ))}
              </div>
            </section>

            {/* Privacy & Security */}
            <section id="privacy-security" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                Privacy & Security
              </h2>
              <div className="space-y-4">
                {faqItems.slice(12, 15).map((item, index) => (
                  <FAQItem
                    key={index + 12}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openIndex === index + 12}
                    onToggle={() => toggleFAQ(index + 12)}
                  />
                ))}
              </div>
            </section>

            {/* Technical Support */}
            <section id="technical-support" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                Technical Support
              </h2>
              <div className="space-y-4">
                {faqItems.slice(15).map((item, index) => (
                  <FAQItem
                    key={index + 15}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openIndex === index + 15}
                    onToggle={() => toggleFAQ(index + 15)}
                  />
                ))}
              </div>
            </section>

            {/* Still Have Questions */}
            <section className="bg-blue-50 dark:bg-blue-900/20 p-8 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Still Have Questions?
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                We're here to help! If you couldn't find the answer you're looking for, please reach out to us directly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="/contact"
                  className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors text-center"
                >
                  Contact Support
                </a>
                <a
                  href="https://forms.gle/Q69fzrKfrVSi5TqW8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-gray-600 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors text-center"
                >
                  Report an Issue
                </a>
              </div>
            </section>
          </div>
        </div>
      </PageLayout>
    </>
  );
}

function FAQItem({ question, answer, isOpen, onToggle }: { question: string; answer: string; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full text-left px-6 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-between"
        aria-expanded={isOpen}
      >
        <span className="font-semibold text-gray-900 dark:text-gray-100 pr-4">{question}</span>
        <svg
          className={`w-5 h-5 text-gray-600 dark:text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-700">
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/faq')({
  component: FAQPage,
});
